import React, { useState, useEffect } from 'react';
import {
    FileText,
    Plus,
    Search,
    Edit,
    Trash2,
    RefreshCw,
    ExternalLink,
    Image as ImageIcon,
    CheckCircle,
    Building
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useModal } from '../ui/ModalProvider';

const CaseStudyManagement = () => {
    const { showConfirm } = useModal();
    const [caseStudies, setCaseStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const { toast } = useToast();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        client: '',
        image: '',
        category: '',
        results: ''
    });

    useEffect(() => {
        loadCaseStudies();
    }, []);

    const loadCaseStudies = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getAdminCaseStudies();
            setCaseStudies(response.data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load case studies",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            description: item.description,
            client: item.client || '',
            image: item.image || '',
            category: item.category || '',
            results: item.results ? item.results.join('\n') : ''
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        showConfirm('Are you sure you want to delete this case study?', async () => {
            try {
                await adminApi.deleteCaseStudy(id);
                setCaseStudies(prev => prev.filter(p => p.id !== id));
                toast({ title: "Deleted", description: "Case study removed successfully" });
            } catch (error) {
                toast({ title: "Error", description: "Failed to delete case study", variant: "destructive" });
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            ...formData,
            results: formData.results.split('\n').map(r => r.trim()).filter(r => r !== '')
        };

        try {
            if (editingItem) {
                const response = await adminApi.updateCaseStudy(editingItem.id, data);
                setCaseStudies(prev => prev.map(p => p.id === editingItem.id ? response.data : p));
                toast({ title: "Updated", description: "Case study updated successfully" });
            } else {
                const response = await adminApi.createCaseStudy(data);
                setCaseStudies(prev => [response.data, ...prev]);
                toast({ title: "Created", description: "Case study created successfully" });
            }
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (error) {
            toast({ title: "Error", description: "Failed to save case study", variant: "destructive" });
        }
    };

    const filtered = caseStudies.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.client && p.client.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-silver mb-2">Case Studies</h1>
                    <p className="text-gray-400">Manage detailed results and success stories</p>
                </div>
                <button
                    onClick={() => {
                        setEditingItem(null);
                        setFormData({ title: '', description: '', client: '', image: '', category: '', results: '' });
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-silver text-black rounded-lg hover:bg-white transition-colors font-medium text-sm"
                >
                    <Plus size={18} />
                    New Case Study
                </button>
            </div>

            <div className="glass-morphism rounded-xl p-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search case studies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver focus:border-silver outline-none"
                    />
                </div>
            </div>

            <div className="grid gap-6">
                {filtered.map((item) => (
                    <div key={item.id} className="glass-morphism rounded-xl overflow-hidden flex flex-col md:flex-row border border-white/5 hover:border-white/10 transition-colors">
                        <div className="w-full md:w-64 aspect-video md:aspect-auto bg-gray-800 relative">
                            {item.image ? (
                                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-600">
                                    <ImageIcon size={40} />
                                </div>
                            )}
                            <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded text-xs text-silver font-medium">
                                {item.category || 'General'}
                            </div>
                        </div>

                        <div className="p-6 flex-1 flex flex-col space-y-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-xl font-bold text-silver mb-1">{item.title}</h3>
                                    <div className="flex items-center gap-2 text-sm text-gray-400">
                                        <Building size={14} />
                                        {item.client || 'Internal Project'}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(item)} className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors">
                                        <Edit size={18} />
                                    </button>
                                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>

                            <p className="text-gray-300 text-sm line-clamp-3">{item.description}</p>

                            {item.results && item.results.length > 0 && (
                                <div className="flex flex-wrap gap-3">
                                    {item.results.map((res, i) => (
                                        <div key={i} className="flex items-center gap-1.5 text-xs text-green-400 bg-green-400/5 px-2 py-1 rounded-full border border-green-400/10">
                                            <CheckCircle size={10} />
                                            {res}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div className="text-center py-20 glass-morphism rounded-xl">
                        <FileText size={48} className="mx-auto text-gray-600 mb-4" />
                        <p className="text-gray-400">No case studies found.</p>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="glass-morphism w-full max-w-2xl rounded-2xl border border-gray-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-silver">
                                {editingItem ? 'Edit Case Study' : 'Create Case Study'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <Plus size={24} className="rotate-45" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Title</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="E.g. Enterprise Cloud Transformation"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Client Name</label>
                                    <input
                                        type="text"
                                        value={formData.client}
                                        onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="E.g. Global Tech Inc."
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Description</label>
                                <textarea
                                    required
                                    rows={4}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none resize-none"
                                    placeholder="Detailed project summary..."
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Category</label>
                                    <input
                                        type="text"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="E.g. Web Dev, AI, Marketing"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Image URL</label>
                                    <input
                                        type="text"
                                        value={formData.image}
                                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="https://..."
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Results / Key Metrics (One per line)</label>
                                <textarea
                                    rows={3}
                                    value={formData.results}
                                    onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                                    className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none resize-none"
                                    placeholder="E.g. 40% Increase in Speed\n2x Higher Conversion"
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 bg-silver text-black font-bold rounded-lg hover:bg-white transition-colors"
                                >
                                    {editingItem ? 'Save Changes' : 'Create Case Study'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaseStudyManagement;
